import { jsPDF } from "jspdf";
import type {
  LiquidityRatios,
  LeverageRatios,
  ProfitabilityRatios,
  EfficiencyRatios,
} from "@shared/financial-types";

interface BalanceSheetData {
  assets: {
    current: {
      cash: number;
      receivables: number;
      inventory: number;
      other: number;
      total: number;
    };
    fixed: {
      tangible: number;
      intangible: number;
      financial: number;
      total: number;
    };
    total: number;
  };
  liabilities: {
    current: {
      payables: number;
      shortTermDebt: number;
      accruals: number;
      other: number;
      total: number;
    };
    longTerm: {
      longTermDebt: number;
      provisions: number;
      other: number;
      total: number;
    };
    total: number;
  };
  equity: {
    capital: number;
    reserves: number;
    retainedEarnings: number;
    currentYearProfit: number;
    total: number;
  };
}

interface ProfitLossData {
  period: { startDate: number; endDate: number };
  revenue: { sales: number; services: number; other: number; total: number };
  expenses: {
    materials: number;
    personnel: number;
    operating: number;
    depreciation: number;
    financial: number;
  };
  result: {
    grossProfit: number;
    operatingProfit: number;
    netProfit: number;
  };
}

interface RatiosData {
  liquidity: LiquidityRatios;
  leverage: LeverageRatios;
  profitability: ProfitabilityRatios;
  efficiency: EfficiencyRatios;
  period: { startDate: number; endDate: number };
}

export function exportBalanceSheetPDF(
  data: BalanceSheetData,
  companyName: string,
  asOfDate: number
) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 20;

  // Header
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("Bilanz / Balance Sheet", pageWidth / 2, y, { align: "center" });

  y += 10;
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.text(companyName, pageWidth / 2, y, { align: "center" });

  y += 7;
  doc.setFontSize(10);
  doc.text(
    `Per / As of: ${new Date(asOfDate).toLocaleDateString("de-CH")}`,
    pageWidth / 2,
    y,
    { align: "center" }
  );

  y += 15;

  // Assets
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("AKTIVEN / ASSETS", 20, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");

  // Current Assets
  doc.setFont("helvetica", "bold");
  doc.text("Umlaufvermögen / Current Assets", 25, y);
  doc.text(formatCHF(data.assets.current.total), pageWidth - 20, y, {
    align: "right",
  });
  y += 6;

  doc.setFont("helvetica", "normal");
  doc.text("Flüssige Mittel / Cash", 30, y);
  doc.text(formatCHF(data.assets.current.cash), pageWidth - 20, y, {
    align: "right",
  });
  y += 5;

  doc.text("Forderungen / Receivables", 30, y);
  doc.text(formatCHF(data.assets.current.receivables), pageWidth - 20, y, {
    align: "right",
  });
  y += 5;

  doc.text("Vorräte / Inventory", 30, y);
  doc.text(formatCHF(data.assets.current.inventory), pageWidth - 20, y, {
    align: "right",
  });
  y += 5;

  doc.text("Übrige / Other", 30, y);
  doc.text(formatCHF(data.assets.current.other), pageWidth - 20, y, {
    align: "right",
  });
  y += 8;

  // Fixed Assets
  doc.setFont("helvetica", "bold");
  doc.text("Anlagevermögen / Fixed Assets", 25, y);
  doc.text(formatCHF(data.assets.fixed.total), pageWidth - 20, y, {
    align: "right",
  });
  y += 6;

  doc.setFont("helvetica", "normal");
  doc.text("Sachanlagen / Tangible Assets", 30, y);
  doc.text(formatCHF(data.assets.fixed.tangible), pageWidth - 20, y, {
    align: "right",
  });
  y += 5;

  doc.text("Immaterielle Anlagen / Intangible", 30, y);
  doc.text(formatCHF(data.assets.fixed.intangible), pageWidth - 20, y, {
    align: "right",
  });
  y += 5;

  doc.text("Finanzanlagen / Financial Assets", 30, y);
  doc.text(formatCHF(data.assets.fixed.financial), pageWidth - 20, y, {
    align: "right",
  });
  y += 8;

  // Total Assets
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("TOTAL AKTIVEN / TOTAL ASSETS", 20, y);
  doc.text(formatCHF(data.assets.total), pageWidth - 20, y, { align: "right" });
  y += 15;

  // Liabilities & Equity
  doc.setFontSize(12);
  doc.text("PASSIVEN / LIABILITIES & EQUITY", 20, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");

  // Current Liabilities
  doc.setFont("helvetica", "bold");
  doc.text("Kurzfristiges Fremdkapital / Current Liabilities", 25, y);
  doc.text(formatCHF(data.liabilities.current.total), pageWidth - 20, y, {
    align: "right",
  });
  y += 6;

  doc.setFont("helvetica", "normal");
  doc.text("Verbindlichkeiten / Payables", 30, y);
  doc.text(formatCHF(data.liabilities.current.payables), pageWidth - 20, y, {
    align: "right",
  });
  y += 5;

  doc.text("Kurzfristige Schulden / Short-term Debt", 30, y);
  doc.text(
    formatCHF(data.liabilities.current.shortTermDebt),
    pageWidth - 20,
    y,
    { align: "right" }
  );
  y += 5;

  doc.text("Rückstellungen / Accruals", 30, y);
  doc.text(formatCHF(data.liabilities.current.accruals), pageWidth - 20, y, {
    align: "right",
  });
  y += 8;

  // Long-term Liabilities
  doc.setFont("helvetica", "bold");
  doc.text("Langfristiges Fremdkapital / Long-term Liabilities", 25, y);
  doc.text(formatCHF(data.liabilities.longTerm.total), pageWidth - 20, y, {
    align: "right",
  });
  y += 6;

  doc.setFont("helvetica", "normal");
  doc.text("Langfristige Schulden / Long-term Debt", 30, y);
  doc.text(
    formatCHF(data.liabilities.longTerm.longTermDebt),
    pageWidth - 20,
    y,
    { align: "right" }
  );
  y += 5;

  doc.text("Rückstellungen / Provisions", 30, y);
  doc.text(formatCHF(data.liabilities.longTerm.provisions), pageWidth - 20, y, {
    align: "right",
  });
  y += 8;

  // Equity
  doc.setFont("helvetica", "bold");
  doc.text("Eigenkapital / Equity", 25, y);
  doc.text(formatCHF(data.equity.total), pageWidth - 20, y, { align: "right" });
  y += 6;

  doc.setFont("helvetica", "normal");
  doc.text("Kapital / Capital", 30, y);
  doc.text(formatCHF(data.equity.capital), pageWidth - 20, y, {
    align: "right",
  });
  y += 5;

  doc.text("Reserven / Reserves", 30, y);
  doc.text(formatCHF(data.equity.reserves), pageWidth - 20, y, {
    align: "right",
  });
  y += 5;

  doc.text("Gewinnvortrag / Retained Earnings", 30, y);
  doc.text(formatCHF(data.equity.retainedEarnings), pageWidth - 20, y, {
    align: "right",
  });
  y += 5;

  doc.text("Jahresgewinn / Current Year Profit", 30, y);
  doc.text(formatCHF(data.equity.currentYearProfit), pageWidth - 20, y, {
    align: "right",
  });
  y += 8;

  // Total Liabilities & Equity
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("TOTAL PASSIVEN / TOTAL LIABILITIES & EQUITY", 20, y);
  doc.text(
    formatCHF(data.liabilities.total + data.equity.total),
    pageWidth - 20,
    y,
    { align: "right" }
  );

  // Save
  doc.save(
    `Bilanz_${companyName}_${new Date(asOfDate).toISOString().split("T")[0]}.pdf`
  );
}

export function exportProfitLossPDF(data: ProfitLossData, companyName: string) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 20;

  // Header
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("Erfolgsrechnung / Profit & Loss", pageWidth / 2, y, {
    align: "center",
  });

  y += 10;
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.text(companyName, pageWidth / 2, y, { align: "center" });

  y += 7;
  doc.setFontSize(10);
  const startDate = new Date(data.period.startDate).toLocaleDateString("de-CH");
  const endDate = new Date(data.period.endDate).toLocaleDateString("de-CH");
  doc.text(`Periode / Period: ${startDate} - ${endDate}`, pageWidth / 2, y, {
    align: "center",
  });

  y += 15;

  // Revenue
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("ERTRAG / REVENUE", 20, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");

  doc.text("Warenertrag / Sales Revenue", 25, y);
  doc.text(formatCHF(data.revenue.sales), pageWidth - 20, y, {
    align: "right",
  });
  y += 5;

  doc.text("Dienstleistungsertrag / Service Revenue", 25, y);
  doc.text(formatCHF(data.revenue.services), pageWidth - 20, y, {
    align: "right",
  });
  y += 5;

  doc.text("Übrige Erträge / Other Revenue", 25, y);
  doc.text(formatCHF(data.revenue.other), pageWidth - 20, y, {
    align: "right",
  });
  y += 6;

  doc.setFont("helvetica", "bold");
  doc.text("Gesamtertrag / Total Revenue", 25, y);
  doc.text(formatCHF(data.revenue.total), pageWidth - 20, y, {
    align: "right",
  });
  y += 12;

  // Expenses
  doc.setFontSize(12);
  doc.text("AUFWAND / EXPENSES", 20, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");

  doc.text("Materialaufwand / Material Expenses", 25, y);
  doc.text(formatCHF(data.expenses.materials), pageWidth - 20, y, {
    align: "right",
  });
  y += 5;

  doc.text("Personalaufwand / Personnel Expenses", 25, y);
  doc.text(formatCHF(data.expenses.personnel), pageWidth - 20, y, {
    align: "right",
  });
  y += 5;

  doc.text("Betriebsaufwand / Operating Expenses", 25, y);
  doc.text(formatCHF(data.expenses.operating), pageWidth - 20, y, {
    align: "right",
  });
  y += 5;

  doc.text("Abschreibungen / Depreciation", 25, y);
  doc.text(formatCHF(data.expenses.depreciation), pageWidth - 20, y, {
    align: "right",
  });
  y += 5;

  doc.text("Finanzaufwand / Financial Expenses", 25, y);
  doc.text(formatCHF(data.expenses.financial), pageWidth - 20, y, {
    align: "right",
  });
  y += 10;

  // Results
  doc.setFont("helvetica", "bold");
  doc.text("Bruttogewinn / Gross Profit", 25, y);
  doc.text(formatCHF(data.result.grossProfit), pageWidth - 20, y, {
    align: "right",
  });
  y += 6;

  doc.text("Betriebsgewinn / Operating Profit", 25, y);
  doc.text(formatCHF(data.result.operatingProfit), pageWidth - 20, y, {
    align: "right",
  });
  y += 6;

  doc.setFontSize(11);
  doc.text("JAHRESGEWINN / NET PROFIT", 20, y);
  doc.text(formatCHF(data.result.netProfit), pageWidth - 20, y, {
    align: "right",
  });

  // Save
  doc.save(
    `Erfolgsrechnung_${companyName}_${new Date(data.period.endDate).toISOString().split("T")[0]}.pdf`
  );
}

export function exportRatiosPDF(data: RatiosData, companyName: string) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 20;

  // Header
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("Kennzahlen / Financial Ratios", pageWidth / 2, y, {
    align: "center",
  });

  y += 10;
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.text(companyName, pageWidth / 2, y, { align: "center" });

  y += 7;
  doc.setFontSize(10);
  const startDate = new Date(data.period.startDate).toLocaleDateString("de-CH");
  const endDate = new Date(data.period.endDate).toLocaleDateString("de-CH");
  doc.text(`Periode / Period: ${startDate} - ${endDate}`, pageWidth / 2, y, {
    align: "center",
  });

  y += 15;

  // Liquidity Ratios
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("LIQUIDITÄT / LIQUIDITY", 20, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");

  doc.text("Current Ratio", 25, y);
  doc.text(data.liquidity.currentRatio.value.toFixed(2), pageWidth - 60, y, {
    align: "right",
  });
  doc.text(
    `(Target: ${data.liquidity.currentRatio.benchmark.target})`,
    pageWidth - 20,
    y,
    { align: "right" }
  );
  y += 5;

  doc.text("Quick Ratio", 25, y);
  doc.text(data.liquidity.quickRatio.value.toFixed(2), pageWidth - 60, y, {
    align: "right",
  });
  doc.text(
    `(Target: ${data.liquidity.quickRatio.benchmark.target})`,
    pageWidth - 20,
    y,
    { align: "right" }
  );
  y += 5;

  doc.text("Cash Ratio", 25, y);
  doc.text(data.liquidity.cashRatio.value.toFixed(2), pageWidth - 60, y, {
    align: "right",
  });
  doc.text(
    `(Target: ${data.liquidity.cashRatio.benchmark.target})`,
    pageWidth - 20,
    y,
    { align: "right" }
  );
  y += 10;

  // Leverage Ratios
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("VERSCHULDUNG / LEVERAGE", 20, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");

  doc.text("Debt-to-Equity", 25, y);
  doc.text(data.leverage.debtToEquity.value.toFixed(2), pageWidth - 60, y, {
    align: "right",
  });
  doc.text(
    `(Target: ${data.leverage.debtToEquity.benchmark.target})`,
    pageWidth - 20,
    y,
    { align: "right" }
  );
  y += 5;

  doc.text("Debt Ratio", 25, y);
  doc.text(
    `${(data.leverage.debtRatio.value * 100).toFixed(1)}%`,
    pageWidth - 60,
    y,
    { align: "right" }
  );
  doc.text(
    `(Target: ${(data.leverage.debtRatio.benchmark.target * 100).toFixed(0)}%)`,
    pageWidth - 20,
    y,
    { align: "right" }
  );
  y += 10;

  // Profitability Ratios
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("RENTABILITÄT / PROFITABILITY", 20, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");

  doc.text("Gross Margin", 25, y);
  doc.text(
    `${data.profitability.grossMargin.value.toFixed(1)}%`,
    pageWidth - 60,
    y,
    { align: "right" }
  );
  doc.text(
    `(Target: ${data.profitability.grossMargin.benchmark.target}%)`,
    pageWidth - 20,
    y,
    { align: "right" }
  );
  y += 5;

  doc.text("Operating Margin", 25, y);
  doc.text(
    `${data.profitability.operatingMargin.value.toFixed(1)}%`,
    pageWidth - 60,
    y,
    { align: "right" }
  );
  doc.text(
    `(Target: ${data.profitability.operatingMargin.benchmark.target}%)`,
    pageWidth - 20,
    y,
    { align: "right" }
  );
  y += 5;

  doc.text("Net Margin", 25, y);
  doc.text(
    `${data.profitability.netMargin.value.toFixed(1)}%`,
    pageWidth - 60,
    y,
    { align: "right" }
  );
  doc.text(
    `(Target: ${data.profitability.netMargin.benchmark.target}%)`,
    pageWidth - 20,
    y,
    { align: "right" }
  );
  y += 5;

  doc.text("ROE (Return on Equity)", 25, y);
  doc.text(`${data.profitability.roe.value.toFixed(1)}%`, pageWidth - 60, y, {
    align: "right",
  });
  doc.text(
    `(Target: ${data.profitability.roe.benchmark.target}%)`,
    pageWidth - 20,
    y,
    { align: "right" }
  );
  y += 5;

  doc.text("ROA (Return on Assets)", 25, y);
  doc.text(`${data.profitability.roa.value.toFixed(1)}%`, pageWidth - 60, y, {
    align: "right",
  });
  doc.text(
    `(Target: ${data.profitability.roa.benchmark.target}%)`,
    pageWidth - 20,
    y,
    { align: "right" }
  );
  y += 10;

  // Efficiency Ratios
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("EFFIZIENZ / EFFICIENCY", 20, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");

  doc.text("Asset Turnover", 25, y);
  doc.text(
    `${data.efficiency.assetTurnover.value.toFixed(2)}x`,
    pageWidth - 60,
    y,
    { align: "right" }
  );
  doc.text(
    `(Target: ${data.efficiency.assetTurnover.benchmark.target}x)`,
    pageWidth - 20,
    y,
    { align: "right" }
  );
  y += 5;

  doc.text("Days Receivables Outstanding", 25, y);
  doc.text(
    `${data.efficiency.daysReceivablesOutstanding.value.toFixed(0)} days`,
    pageWidth - 60,
    y,
    { align: "right" }
  );
  doc.text(
    `(Target: ${data.efficiency.daysReceivablesOutstanding.benchmark.target} days)`,
    pageWidth - 20,
    y,
    { align: "right" }
  );
  y += 5;

  // Save
  doc.save(
    `Kennzahlen_${companyName}_${new Date(data.period.endDate).toISOString().split("T")[0]}.pdf`
  );
}

function formatCHF(amount: number): string {
  return new Intl.NumberFormat("de-CH", {
    style: "currency",
    currency: "CHF",
  }).format(amount / 100);
}
